<script setup lang="ts">
import Logo from '../components/LogoComponent.vue';
import { computed, onMounted, reactive, ref, VueElement, watch } from 'vue';
import { useRoute } from 'vue-router';
import { menu } from '../json/menuitems.json';
import { Icons } from '../classes';
import type { icon } from '../types/Icon';

defineProps({
  currentViewIndex: {
    type: Number,
    required: false,
  },
});

const route = useRoute();

const handleClick = (me: MouseEvent) => {
  if (me.target instanceof HTMLLIElement || me.currentTarget instanceof HTMLLIElement) {
    const anchor = (
      me.target instanceof HTMLLIElement ? me.target : (me.currentTarget as HTMLLIElement)
    ).querySelector('a#menu-item-link') as HTMLAnchorElement;
    anchor?.click?.();
  }
};

const menuData = reactive({
  menu,
});
const logodata = computed(() => {
  return {
    src: menuData.menu.logo.src,
    alt: menuData.menu.logo.alt,
    href: menuData.menu.logo.href,
  };
});
const dataitems = computed<{
  icon: {
    name: string;
    width: string;
  } | VueElement;
  path: string;
  content: string;
}[]>(() => {
  return menuData.menu.items.map((item) => {
    return {
      ...item,
      icon: item.icon.name.search(/\.(svg|png|jpg|jpeg)/) > 0 ? item.icon : Icons[item.icon.name],
    };
  });
});

// Precompute icon paths for all menu items
const iconPaths = computed(() => {
  return dataitems.value.map((item) => {
    return `/assets/images/backgrounds/${(item.icon as icon).name}`;
  });
});

const clientWidth = ref({
  newWidth: window.screen.width,
  newHeight: window.screen.height,
});

const sizes = reactive({
  width: '1.65em',
  height: '1.65em',
});

watch(clientWidth, ({ newHeight, newWidth }) => {
  if (newWidth <= 425) {
    sizes.width = '1.15em';
  } else if (newWidth <= 768) {
    sizes.width = '1.55em';
  } else if (newWidth <= 1024) {
    sizes.width = '1.95em';
  } else if (newWidth <= 1440) {
    sizes.width = '5.15em';
  } else {
    sizes.width = '35em';
  }

  if (newHeight <= 425) {
    sizes.height = '.85rem';
  } else if (newHeight <= 768) {
    sizes.height = '1.15rem';
  } else if (newHeight <= 1024) {
    sizes.height = '9.35rem';
  } else {
    sizes.height = '1.65rem';
  }
});

const tooltipContent = ref('');
const dialog = ref(document.createElement('dialog') as HTMLDialogElement);
const span = ref(document.createElement('span') as HTMLSpanElement);
const reactiveContent = reactive(tooltipContent);
onMounted(() => {
  if (dialog.value) {
    dialog.value.popover = 'manual';
    dialog.value.style.position = 'fixed';
    dialog.value.style.right = 'auto';
    dialog.value.style.bottom = 'auto';
    dialog.value.style.borderRadius = '0.55rem';
    dialog.value.style.padding = '0.25rem';
    dialog.value.style.boxShadow = '0 0 9px rgba(0, 0, 0, 0.5)';
    dialog.value.style.color = 'var(--pya-text-black)';
    dialog.value.style.fontSize = '1.065rem';
    dialog.value.style.fontWeight = '500';
    dialog.value.style.textAlign = 'center';
    dialog.value.style.backgroundColor = 'var(--pya-chart-sequential-1)';
    span.value.addEventListener(
      'changecontent',
      (ev) => ((ev.target as HTMLSpanElement).innerText = reactiveContent.value)
    );
    dialog.value.appendChild(span.value);
    dialog.value.style.zIndex = '9999';
    document.body.appendChild(dialog.value);
  }
  document.addEventListener('mouseover', () => {
    if (dialog.value.hasAttribute('open')) {
      dialog.value.removeAttribute('open');
      dialog.value.togglePopover();
    }
  });
});
const handleMouseOver = (me: MouseEvent) => {
  let button: HTMLButtonElement | null = null;
  if (me.target instanceof HTMLButtonElement || me.currentTarget instanceof HTMLButtonElement) {
    button =
      me.target instanceof HTMLButtonElement
        ? me.target
        : (me.currentTarget as HTMLButtonElement);
    tooltipContent.value = button.name;
    span.value.dispatchEvent(
      new CustomEvent('changecontent', {
        bubbles: true,
        composed: true,
        detail: { content: reactiveContent.value },
      })
    );
  }
  if (!tooltipContent.value || tooltipContent.value.trim() === '') {
    return;
  }
  if (dialog.value) {
    dialog.value.style.top = me.clientY + (button?.offsetHeight ?? 0) + 'px';
    dialog.value.style.left = me.clientX - (button?.offsetWidth ?? 0) + 5 + 'px';
    dialog.value.setAttribute('open', '');
    dialog.value.togglePopover();
  }
};

const handleMouseLeave = () => {
  tooltipContent.value = '';
  span.value.dispatchEvent(
    new CustomEvent('changecontent', {
      bubbles: true,
      composed: true,
      detail: { content: reactiveContent.value },
    })
  );
  if (dialog.value) {
    dialog.value.removeAttribute('open');
    dialog.value.togglePopover();
  }
};
</script>

<template>
  <section x-placement="top" id="menu1-0" class="view-menu">
    <Logo :logo="logodata.src" :alt="logodata.alt" :href="logodata.href"
      class="shadow-md rounded" />
    <nav id="navbarSupportedContent" class="view-menu-nav">
      <ul class="view-menu-list">
        <li v-for="(item, index) in dataitems" :key="index" class="view-menu-btn"
          @click="handleClick" :class="{ active: route?.path === item?.path }">
          <button class="menu-icon-button" :name="item.content"
            @mouseleave="handleMouseLeave" @mouseenter="handleMouseOver" :style="{
              width: '100% !important',
              backgroundColor: /pugalogoblanco\.svg/.test((item.icon as icon).name) ? '#28ACD0FF' : 'transparent',
              border: 0,
              marginInline: /pugalogoblanco\.svg/.test((item.icon as icon).name) ? '0' : '.5rem',
              backgroundImage: `url(${iconPaths[index]})`,
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              cursor: 'inherit'
            }">
            <RouterLink :to="item.path" id="menu-item-link">
            </RouterLink>
          </button>
        </li>
      </ul>
    </nav>
  </section>
</template>

<style scoped>
  @import url('/assets/styles/menucomponent.css');

  #menu-item-link {
    width: 100% !important;
    max-width: calc(90vw * 0.99);
    min-width: 100px;
  }

  @media (max-width: 420px) and (max-height: 768px) {
    #menu-item-link {
      width: inherit !important;
      max-width: calc(100vw * 0.039) !important;
      min-width: 10px;
    }

    .view-menu-list {
      flex-flow: row nowrap !important;
      justify-content: space-around !important;
      align-items: center !important;
      margin: auto !important;
    }

    .menu-icon-button {
      width: inherit !important;
      max-width: calc(100vw * 0.39) !important;
      border: 0;
      height: inherit !important;
      background-size: 50% !important;
      background-position: center !important;
      margin-inline: 1rem;
      color: transparent;
    }

    .view-menu-btn {
      max-width: calc(100vw * 0.4) !important;
      min-width: calc(50vw * 0.2) !important;
      scale: 1.1;
      min-height: calc(40vh * 0.1) !important;
      margin: inherit !important;
    }
  }

  @media (max-width: 1024px) and (max-height: 768px) {
    #menu-item-link {
      width: auto !important;
      max-width: calc(90vw * 0.39) !important;
      min-width: 10px;
    }

    .view-menu-btn {
      max-width: calc(100vw * 0.04);
    }

    .menu-icon-button {
      width: inherit !important;
      max-width: calc(100vw * 0.39) !important;
      border: 0;
      height: inherit !important;
      background-size: 50% !important;
      background-position: center !important;
      margin-inline: 1rem;
      color: transparent;
    }

    .view-menu-list {
      flex-flow: row nowrap !important;
      justify-content: space-around !important;
      align-items: center !important;
      margin: auto !important;
    }
  }

  .view-menu-btn {
    max-width: calc(100vw * 0.04);
  }

  .menu-icon-button {
    width: inherit;
    max-width: calc(100vw * 0.39);
    border: 0;
    height: inherit;
    background-size: 50%;
    background-position: center;
    margin-inline: 1rem;
    color: transparent;
  }

  .menu-icon-button:active,
  .menu-icon-button:focus,
  .menu-icon-button:is(:active, :focus) {
    outline: none;
  }

  .menu-icon-button:hover {
    color: inherit;
  }
</style>